import React, { Fragment } from 'react';
import styled from 'styled-components';
import { compose, variant } from 'styled-system';

import { type FontWeightProps, fontWeight } from '@rover/kibble/styles/styled-system';
import type { ThemeTextSizes } from '@rover/kibble/styles/themeTypes';
import type { Breadcrumb } from '@rover/rsdk/src/apiClient/latest/model/breadcrumb';

const sizeProp = {
  prop: 'size',
  variants: {
    100: {
      fontSize: ({ theme }) => theme.text['100'].fontSize,
      lineHeight: ({ theme }) => theme.text['100'].lineHeight,
    },
    200: {
      fontSize: ({ theme }) => theme.text['200'].fontSize,
      lineHeight: ({ theme }) => theme.text['200'].lineHeight,
    },
    300: {
      fontSize: ({ theme }) => theme.text['300'].fontSize,
      lineHeight: ({ theme }) => theme.text['300'].lineHeight,
    },
  },
};

const BreadcrumbList = styled.ol<{ isOnTopOfPage: boolean }>`
  text-align: left;
  list-style: none;
  padding-left: 0;
  margin-left: 0;
  margin-bottom: ${(props) => (props.isOnTopOfPage ? props.theme.space['4x'] : 0)};
  margin-top: ${(props) => (props.isOnTopOfPage ? 0 : props.theme.space['6x'])};
  & > li:first-child {
    padding-left: 0px;
  }
`;

const BreadcrumbItem = styled('li')`
  display: inline-block;
  padding: 0 ${({ theme }) => theme.space['1x']};
  ${variant(sizeProp)}
  ${compose(fontWeight)}
`;
const BreadcrumbSeparator = styled('li')`
  display: inline-block;
  padding: 0 ${({ theme }) => theme.space['1x']};
  ${variant(sizeProp)}
  ${compose(fontWeight)}
`;

export type Props = FontWeightProps & {
  breadcrumbData: Breadcrumb[];
  separator?: string;
  size?: ThemeTextSizes;
  color?: string;
  underline?: boolean;
  separatorStyle?: React.CSSProperties;
  isOnTopOfPage?: boolean;
  lastItemIsCurrentPage?: boolean;
};

const Breadcrumbs = ({
  breadcrumbData,
  separator = '>',
  color,
  underline = false,
  separatorStyle,
  isOnTopOfPage = false,
  lastItemIsCurrentPage = false,
  ...others
}: Props): JSX.Element => (
  <nav aria-label="Breadcrumbs">
    <BreadcrumbList
      isOnTopOfPage={isOnTopOfPage}
      itemScope
      itemType="http://schema.org/BreadcrumbList"
    >
      {breadcrumbData.map((anchor, index) => {
        const isLastItem = breadcrumbData.length === index + 1;
        // renderAsCurrentPage is meant to render the item with no link attribute
        const renderAsCurrentPage = isLastItem && lastItemIsCurrentPage;
        return (
          <Fragment key={anchor.href}>
            <BreadcrumbItem
              itemProp="itemListElement"
              itemScope
              itemType="http://schema.org/ListItem"
              aria-current={isLastItem ? 'page' : undefined}
              {...others}
            >
              {renderAsCurrentPage ? (
                <span itemProp="item">
                  <span itemProp="name" style={{ color }} aria-current="page">
                    {anchor.title}
                  </span>
                </span>
              ) : (
                <a
                  href={anchor.href}
                  itemProp="item"
                  style={{ color, textDecoration: underline ? 'underline' : 'none' }}
                  aria-current={isLastItem ? 'page' : undefined}
                >
                  <span itemProp="name">{anchor.title}</span>
                </a>
              )}
              <meta itemProp="position" content={`${index + 1}`} />
            </BreadcrumbItem>
            {!isLastItem && (
              <BreadcrumbSeparator style={{ color, ...separatorStyle }} {...others} aria-hidden>
                {separator}
              </BreadcrumbSeparator>
            )}
          </Fragment>
        );
      })}
    </BreadcrumbList>
  </nav>
);

export default Breadcrumbs;
